import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit, FiTrash, FiCalendar } from 'react-icons/fi';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const ExpenseManagement = () => {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: ''
  });
  const [totalExpense, setTotalExpense] = useState(0);
  const [inventoryCategories, setInventoryCategories] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [expenseForm, setExpenseForm] = useState({
    title: '',
    description: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    paymentMethod: 'cash',
    notes: '',
    // Inventory fields
    categoryId: '',
    itemId: '',
    quantity: '',
    unit: '',
    originalUnit: '',
    price: '',
    isInventoryExpense: false
  });

  // Get conversion options for radio buttons
  const getConversionOptions = (originalUnit) => {
    const unitOptions = {
      'kg': [
        { value: 'kg', label: 'kg' },
        { value: 'g', label: 'g', factor: 1000 }
      ],
      'g': [
        { value: 'kg', label: 'kg', factor: 0.001 },
        { value: 'g', label: 'g', factor: 1 }
      ],
      'l': [
        { value: 'l', label: 'l' },
        { value: 'ml', label: 'ml', factor: 1000 }
      ],
      'ml': [
        { value: 'l', label: 'l', factor: 0.001 },
        { value: 'ml', label: 'ml', factor: 1 }
      ],
      'dozen': [
        { value: 'dozen', label: 'dozen' },
        { value: 'pcs', label: 'pcs', factor: 12 }
      ],
      'pcs': [
        { value: 'dozen', label: 'dozen', factor: 0.08333 }, // 1 pcs = 1/12 dozen
        { value: 'pcs', label: 'pcs', factor: 1 }
      ]
      // packet and other units have no conversion options
    };

    return unitOptions[originalUnit?.toLowerCase()] || [
      { value: originalUnit, label: originalUnit, factor: 1 }
    ];
  };


  useEffect(() => {
    fetchExpenses();
    fetchInventoryCategories();
  }, [filters]);

  const fetchInventoryCategories = async () => {
    try {
      const response = await api.get('/vendor/inventory-categories');
      setInventoryCategories(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch inventory categories:', error);
    }
  };

  const fetchInventoryItems = async (categoryId) => {
    try {
      const response = await api.get(`/vendor/inventory-items?categoryId=${categoryId}`);
      setInventoryItems(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch inventory items:', error);
      setInventoryItems([]);
    }
  };

  const handleCategoryChange = (categoryId) => {
    setExpenseForm({ 
      ...expenseForm, 
      categoryId: categoryId,
      itemId: '',
      unit: '',
      originalUnit: '',
      quantity: '' // Reset all item-related fields
    });
    
    // Fetch items for the selected category
    if (categoryId) {
      fetchInventoryItems(categoryId);
    } else {
      setInventoryItems([]);
    }
  };

  const handleItemChange = (itemId) => {

    const selectedItem = inventoryItems.find(item => item._id === itemId);
    const unit = selectedItem?.unit || 'pcs';
    setExpenseForm({ 
      ...expenseForm, 
      itemId: itemId,
      unit: unit,
      originalUnit: unit,
      quantity: '' // Reset quantity when item changes
    });
    
    // Auto-calculate amount if price is set
    if (expenseForm.price) {
      setExpenseForm(prev => ({ 
        ...prev, 
        itemId: itemId,
        unit: unit,
        originalUnit: unit,
        quantity: '',
        amount: '0' // Reset amount since quantity is cleared
      }));
    }
  };

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);

      const response = await api.get(`/vendor/expenses?${params.toString()}`);
      setExpenses(response.data.data || []);
      setTotalExpense(response.data.totalExpense || 0);
    } catch (error) {
      toast.error('Failed to fetch expenses');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!expenseForm.title.trim()) {
      toast.error('Title is required');
      return;
    }
    
    if (!expenseForm.date) {
      toast.error('Date is required');
      return;
    }
    
    if (!expenseForm.paymentMethod) {
      toast.error('Payment method is required');
      return;
    }
    
    // For inventory expenses, validate inventory fields
    if (expenseForm.isInventoryExpense) {
      if (!expenseForm.categoryId) {
        toast.error('Category is required for inventory expenses');
        return;
      }
      if (!expenseForm.itemId) {
        toast.error('Item is required for inventory expenses');
        return;
      }
      if (!expenseForm.quantity || parseFloat(expenseForm.quantity) <= 0) {
        toast.error('Quantity is required and must be greater than 0');
        return;
      }
      if (!expenseForm.price || parseFloat(expenseForm.price) <= 0) {
        toast.error('Price is required and must be greater than 0');
        return;
      }
    } else {
      // For regular expenses, validate amount
      if (!expenseForm.amount || parseFloat(expenseForm.amount) <= 0) {
        toast.error('Amount is required and must be greater than 0');
        return;
      }
    }
    
    try {
      // If it's an inventory expense, update inventory history
      if (expenseForm.isInventoryExpense && expenseForm.itemId && expenseForm.quantity) {
        try {
          await api.patch(`/vendor/inventory-items/${expenseForm.itemId}/quantity`, {
            quantity: parseFloat(expenseForm.quantity),
            transactionType: 'purchase',
            costPerUnit: parseFloat(expenseForm.price) || 0,
            unit: expenseForm.unit,
            originalUnit: expenseForm.originalUnit
          });
          toast.success('Inventory updated successfully');
        } catch (inventoryError) {
          console.error('Failed to update inventory:', inventoryError);
          toast.error('Failed to update inventory, but expense saved');
        }
      }

      if (selectedExpense) {
        await api.put(`/vendor/expenses/${selectedExpense._id}`, expenseForm);
        toast.success('Expense updated successfully');
      } else {
        await api.post('/vendor/expenses', expenseForm);
        toast.success('Expense added successfully');
      }
      setShowModal(false);
      resetForm();
      fetchExpenses();
    } catch (error) {
      toast.error('Failed to save expense');
    }
  };

  const handleDelete = async (expenseId) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) return;
    try {
      await api.delete(`/vendor/expenses/${expenseId}`);
      toast.success('Expense deleted successfully');
      fetchExpenses();
    } catch (error) {
      toast.error('Failed to delete expense');
    }
  };

  const openModal = (expense = null) => {
    if (expense) {
      setSelectedExpense(expense);
      setExpenseForm({
        title: expense.title,
        description: expense.description || '',
        amount: expense.amount,
        date: expense.date?.split('T')[0] || '',
        paymentMethod: expense.paymentMethod || 'cash',
        notes: expense.notes || '',
        // Inventory fields
        isInventoryExpense: expense.isInventoryExpense || false,
        categoryId: expense.categoryId?._id || expense.categoryId || '',
        itemId: expense.itemId?._id || expense.itemId || '',
        quantity: expense.quantity?.toString() || '',
        unit: expense.unit || expense.itemId?.unit || 'pcs',
        originalUnit: expense.originalUnit || expense.itemId?.unit || 'pcs',
        price: expense.price?.toString() || ''
      });
      
      // If it's an inventory expense, fetch items for the category
      if (expense.isInventoryExpense && expense.categoryId) {
        fetchInventoryItems(expense.categoryId._id || expense.categoryId);
      }
    }
    setShowModal(true);
  };

  const resetForm = () => {
    setExpenseForm({
      title: '',
      description: '',
      amount: '',
      date: new Date().toISOString().split('T')[0],
      paymentMethod: 'cash',
      notes: '',
      // Inventory fields
      categoryId: '',
      itemId: '',
      quantity: '',
      unit: '',
      originalUnit: '',
      price: '',
      isInventoryExpense: false
    });
    setInventoryItems([]);
    setSelectedExpense(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-red-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 p-4 md:p-6">
      {/* Page Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Expense Management</h1>
            <p className="text-gray-600">Track and manage restaurant expenses</p>
          </div>
          <button 
            className="px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold rounded-lg hover:shadow-lg transition-all flex items-center space-x-2"
            onClick={() => openModal()}
          >
            <FiPlus className="text-lg" />
            <span>Add Expense</span>
          </button>
        </div>
      </div>

      {/* Summary Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-red-100 rounded-xl flex items-center justify-center">
            <FiCalendar className="text-red-600 text-2xl" />
          </div>
          <div>
            <h3 className="text-3xl font-bold text-red-600">₹{totalExpense.toLocaleString()}</h3>
            <p className="text-gray-600">Total Expenses</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-col lg:flex-row gap-3 items-center flex-wrap">
          <button 
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors duration-200" 
            onClick={() => {
              const today = new Date().toISOString().split('T')[0];
              setFilters({ startDate: today, endDate: today });
            }}
          >
            Today
          </button>
          <button 
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors duration-200" 
            onClick={() => {
              const yesterday = new Date();
              yesterday.setDate(yesterday.getDate() - 1);
              const yesterdayStr = yesterday.toISOString().split('T')[0];
              setFilters({ startDate: yesterdayStr, endDate: yesterdayStr });
            }}
          >
            Yesterday
          </button>
          <div className="flex items-center gap-2">
            <input
              type="date"
              className="px-3 py-2 border border-gray-300 rounded-lg focus:border-red-500 focus:outline-none transition-colors duration-200"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              placeholder="Start Date"
            />
            <span className="text-gray-500">to</span>
            <input
              type="date"
              className="px-3 py-2 border border-gray-300 rounded-lg focus:border-red-500 focus:outline-none transition-colors duration-200"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              placeholder="End Date"
            />
          </div>
          {(filters.startDate || filters.endDate) && (
            <button 
              className="px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg font-medium transition-colors duration-200" 
              onClick={() => setFilters({ startDate: '', endDate: '' })}
              title="Clear filters"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Expenses Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-red-50 to-red-100 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Date</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Title</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Inventory</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Notes</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Payment Method</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {expenses.map((expense) => (
                <tr key={expense._id} className="hover:bg-gray-50 transition-colors duration-150">
                  <td className="px-6 py-4 text-sm text-gray-700">{new Date(expense.date).toLocaleDateString()}</td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{expense.title}</div>
                    {expense.isInventoryExpense && (
                      <div className="flex items-center gap-1 mt-1">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                          📦 Inventory
                        </span>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {expense.isInventoryExpense ? (
                      <div className="text-sm">
                        <div className="font-medium text-gray-900">{expense.itemId?.name || 'Unknown Item'}</div>
                        <div className="text-xs text-gray-500">
                          {expense.quantity} {expense.unit || expense.itemId?.unit || ''} × ₹{expense.price}
                        </div>
                        <div className="text-xs text-blue-600">
                          {expense.categoryId?.name || 'Unknown Category'}
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500">-</div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">{expense.notes || '-'}</td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-bold text-red-600">₹{expense.amount.toLocaleString()}</div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                      expense.paymentMethod === 'cash' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {expense.paymentMethod === 'cash' ? '💵 Cash' : '💳 Online'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      <button 
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200 hover:scale-110"
                        onClick={() => openModal(expense)} 
                        title="Edit Expense"
                      >
                        <FiEdit size={16} />
                      </button>
                      <button 
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200 hover:scale-110"
                        onClick={() => handleDelete(expense._id)} 
                        title="Delete Expense"
                      >
                        <FiTrash size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {expenses.length === 0 && (
          <div className="bg-gray-50 rounded-lg p-12 text-center">
            <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiCalendar className="text-gray-400 text-3xl" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No expenses found</h3>
            <p className="text-gray-600 mb-6">Start by adding your first expense to track your spending.</p>
            <button 
              className="px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold rounded-lg hover:shadow-lg transition-all flex items-center justify-center mx-auto space-x-2"
              onClick={() => openModal()}
            >
              <FiPlus className="text-lg" />
              <span>Add Your First Expense</span>
            </button>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[95vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-red-500 to-red-600 text-white p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold">{selectedExpense ? 'Edit Expense' : 'Add Expense'}</h2>
                <button 
                  className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors duration-200"
                  onClick={() => setShowModal(false)}
                >
                  <FiPlus className="text-xl rotate-45" />
                </button>
              </div>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="p-6 overflow-y-auto max-h-[calc(95vh-140px)]">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                    <input
                      type="text"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-red-500 focus:outline-none transition-colors duration-200"
                      value={expenseForm.title}
                      onChange={(e) => setExpenseForm({ ...expenseForm, title: e.target.value })}
                      placeholder="e.g., Electricity Bill, Staff Salary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Date *</label>
                    <input
                      type="date"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-red-500 focus:outline-none transition-colors duration-200"
                      value={expenseForm.date}
                      onChange={(e) => setExpenseForm({ ...expenseForm, date: e.target.value })}
                    />
                  </div>
                </div>

                {/* Inventory Toggle */}
                <div className="mb-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={expenseForm.isInventoryExpense}
                      onChange={(e) => {
                        setExpenseForm({ 
                          ...expenseForm, 
                          isInventoryExpense: e.target.checked,
                          categoryId: e.target.checked ? expenseForm.categoryId : '',
                          itemId: e.target.checked ? expenseForm.itemId : '',
                          quantity: e.target.checked ? expenseForm.quantity : '',
                          price: e.target.checked ? expenseForm.price : ''
                        });
                        if (!e.target.checked) {
                          setInventoryItems([]);
                        }
                      }}
                      className="w-4 h-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                    />
                    <span className="text-sm font-medium text-gray-700">This is an inventory expense</span>
                  </label>
                </div>

                {/* Inventory Fields */}
                {expenseForm.isInventoryExpense && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <h4 className="text-sm font-semibold text-blue-900 mb-3">Inventory Details</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Category *</label>
                        <select
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-red-500 focus:outline-none transition-colors duration-200"
                          value={expenseForm.categoryId}
                          onChange={(e) => handleCategoryChange(e.target.value)}
                        >
                          <option value="">Select Category</option>
                          {inventoryCategories.filter(cat => cat.isActive).map(cat => (
                            <option key={cat._id} value={cat._id}>{cat.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Item *</label>
                        <select
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-red-500 focus:outline-none transition-colors duration-200"
                          value={expenseForm.itemId}
                          onChange={(e)=>handleItemChange(e.target.value)}
                          disabled={!expenseForm.categoryId}
                        >
                          <option value="">Select Item</option>
                          {inventoryItems.map(item => (
                            <option key={item._id} value={item._id}>{item.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Quantity *</label>
                        <input
                          type="text"
                          inputMode="decimal"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-red-500 focus:outline-none transition-colors duration-200"
                          value={expenseForm.quantity}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                              setExpenseForm({ ...expenseForm, quantity: value });
                              // Auto-calculate amount if price is set
                              if (expenseForm.price && value) {
                                setExpenseForm(prev => ({
                                  ...prev,
                                  quantity: value,
                                  amount: (parseFloat(value) * parseFloat(prev.price)).toFixed(2)
                                }));
                              }
                            }
                          }}
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Price per Unit (Rs) *</label>
                        <input
                          type="text"
                          inputMode="decimal"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-red-500 focus:outline-none transition-colors duration-200"
                          value={expenseForm.price}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                              setExpenseForm({ ...expenseForm, price: value });
                              // Auto-calculate amount if quantity is set
                              if (expenseForm.quantity && value) {
                                setExpenseForm(prev => ({
                                  ...prev,
                                  price: value,
                                  amount: (parseFloat(prev.quantity) * parseFloat(value)).toFixed(2)
                                }));
                              }
                            }
                          }}
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Unit Conversion</label>
                        <div className="space-y-2">
                          {getConversionOptions(expenseForm.originalUnit).map((option) => (
                            <label key={option.value} className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name="unitConversion"
                                value={option.value}
                                checked={expenseForm.unit === option.value}
                                onChange={(e) => {
                                  const newUnit = e.target.value;
                                  const conversionFactor = option.factor;
                                  setExpenseForm(prev => ({
                                    ...prev,
                                    unit: newUnit,
                                    // Convert quantity based on conversion factor
                                    quantity: prev.quantity ? (parseFloat(prev.quantity) * conversionFactor).toFixed(2) : '',
                                    amount: prev.quantity && prev.price ? 
                                      ((parseFloat(prev.quantity) * conversionFactor) * parseFloat(prev.price)).toFixed(2) : 
                                      prev.amount
                                  }));
                                }}
                                className="w-4 h-4 text-red-600 focus:ring-red-500 border-gray-300"
                              />
                              <span className="text-sm text-gray-700">{option.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Regular Expense Amount Field */}
                {!expenseForm.isInventoryExpense && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Amount (Rs) *</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-red-500 focus:outline-none transition-colors duration-200"
                      value={expenseForm.amount}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                          setExpenseForm({ ...expenseForm, amount: value });
                        }
                      }}
                      required
                    />
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method *</label>
                    <select
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-red-500 focus:outline-none transition-colors duration-200"
                      value={expenseForm.paymentMethod}
                      onChange={(e) => setExpenseForm({ ...expenseForm, paymentMethod: e.target.value })}
                    >
                      <option value="cash">Cash</option>
                      <option value="online">Online</option>
                    </select>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                  <textarea
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-red-500 focus:outline-none transition-colors duration-200"
                    value={expenseForm.notes}
                    onChange={(e) => setExpenseForm({ ...expenseForm, notes: e.target.value })}
                    rows="2"
                    placeholder="Additional notes (optional)"
                  />
                </div>
              </div>
              <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end space-x-3 sticky bottom-0">
                <button 
                  type="button" 
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors duration-200"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors duration-200"
                >
                  {selectedExpense ? 'Update' : 'Add'} Expense
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpenseManagement;
